import { useMemo, useState, type FC } from "react";
import ChartSwitcher from "@/components/ChartSwitcher";
import useDarkMode from "@/hooks/useDarkMode";
import {
  buildCreativeComboOption,
  CREATIVE_TOP_N,
} from "@/pages/CampaignOverviewPage/charts/creativeChartHelpers";
import ChartStateBody from "@/pages/CampaignOverviewPage/components/ChartStateBody";
import {
  groupByCreative,
  selectTopCreatives,
} from "@/pages/CampaignOverviewPage/utils/creativeGrouping";
import type { PerformanceReportRow } from "@/types/reporting";
import { TEXT } from "@/constants/text";
import styles from "@/pages/CampaignOverviewPage/widgets/TopPerformers.module.css";

interface CreativeCtrPanelProps {
  error: string | null;
  isLoading: boolean;
  // Group key of the creative the "Top creative by CTR" tile names — always
  // charted, even when it falls outside the top 10 by impressions.
  leaderKey?: string | null;
  rows: PerformanceReportRow[];
}

const PANEL_TEXT = TEXT.campaignOverview.topPerformers;
const CREATIVE_TEXT = TEXT.campaignOverview.creativePerformance;

const TOP_N_OPTIONS = [
  { key: "top", label: PANEL_TEXT.showTop10 },
  { key: "all", label: PANEL_TEXT.showAll },
] as const;

const getSubtitle = (showAll: boolean, leaderAdded: boolean): string => {
  if (showAll) return PANEL_TEXT.creativePanel.subtitleAll;
  return leaderAdded
    ? PANEL_TEXT.creativePanel.subtitleTopWithLeader
    : PANEL_TEXT.creativePanel.subtitleTop;
};

const CHART_STYLE = { height: "100%", width: "100%" } as const;

// Detail panel for the "Top creative by CTR" tile: impressions bars with a CTR
// line, top 10 by default.
const CreativeCtrPanel: FC<CreativeCtrPanelProps> = ({
  error,
  isLoading,
  leaderKey = null,
  rows,
}) => {
  const isDark = useDarkMode();
  const [showAll, setShowAll] = useState(false);
  const option = useMemo(
    () => buildCreativeComboOption(rows, showAll, isDark, leaderKey),
    [rows, showAll, isDark, leaderKey]
  );

  const leaderAdded = useMemo(
    () =>
      selectTopCreatives(groupByCreative(rows), CREATIVE_TOP_N, leaderKey)
        .leaderAdded,
    [rows, leaderKey]
  );

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <div className={styles.titleGroup}>
          <span className={styles.title}>{PANEL_TEXT.creativePanel.title}</span>
          <span className={styles.subtitle}>
            {getSubtitle(showAll, leaderAdded)}
          </span>
        </div>
        <ChartSwitcher
          label={TEXT.campaignOverview.switcher.rowsShown}
          options={TOP_N_OPTIONS}
          value={showAll ? "all" : "top"}
          onChange={(key) => setShowAll(key === "all")}
        />
      </div>
      <div className={styles.chartFrame}>
        <ChartStateBody
          error={error}
          errorMessage={CREATIVE_TEXT.error}
          isEmpty={rows.length === 0}
          isLoading={isLoading}
          loadingClassName={styles.loading}
          messageClassName={styles.message}
          noDataMessage={CREATIVE_TEXT.emptyMessage}
          option={option}
          style={CHART_STYLE}
        />
      </div>
    </div>
  );
};

export default CreativeCtrPanel;
